/**
 * Hook for managing Agent Drawer tab configuration
 *
 * Filters tabs based on user role and computes badge state.
 *
 * @see asset/hooks/drawer/useAssetTabs.ts for reference pattern
 */

import { TabOption } from '@/components/chariot-ui';
import { useMemo } from 'react';

import { AgentTabConfig } from '@/sections/agents/components/drawer/types';
import {
  AGENT_DRAWER_TABS,
  AGENT_DRAWER_TABS_CONFIG,
} from '@/sections/agents/constants';

/**
 * Hook for managing Agent Drawer tab configuration.
 *
 * Filters tabs based on user role (Praetorian vs customer) and
 * computes badge state for the Tasks tab.
 *
 * @param isPraetorianUser - If true, includes Praetorian-only tabs
 * @param hasActiveTasks - If true, shows badge on Tasks tab
 * @returns Array of TabOption for the Tabs component
 *
 * @example
 * ```typescript
 * const { isPraetorianUser } = useAuth();
 * const hasActiveTasks = taskMonitoring.hasActiveTasks();
 * const tabs = useAgentTabs(isPraetorianUser, hasActiveTasks);
 * // Returns: [{ label: 'Overview', value: 'overview' }, ...]
 * ```
 */
export function useAgentTabs(
  isPraetorianUser: boolean,
  hasActiveTasks: boolean
): TabOption[] {
  return useMemo(() => {
    // Filter tabs based on user role
    const visibleTabs = AGENT_DRAWER_TABS_CONFIG.filter(
      tab => !tab.praetorianOnly || isPraetorianUser
    );

    // Map to TabOption[], adding badge for Tasks tab
    return visibleTabs.map((tab: AgentTabConfig): TabOption => {
      const isTasksTab = tab.value === AGENT_DRAWER_TABS.TASKS;

      return {
        label: tab.label,
        value: tab.value,
        // Note: TabOption from chariot-ui may not support badgeCount
        // The badge will be handled by the Tabs component or custom rendering
        ...(isTasksTab && hasActiveTasks
          ? { badgeCount: 1, hasUpdates: true }
          : {}),
      };
    });
  }, [isPraetorianUser, hasActiveTasks]);
}
